import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: '📊' },
  { path: '/cases', label: 'My Tickets', icon: '🎫' },
  { path: '/invoices', label: 'Invoices', icon: '💰' },
  { path: '/m365-users', label: 'Users & Licenses', icon: '👥' },
  { path: '/security-posture', label: 'Security', icon: '🛡️' },
  { path: '/settings', label: 'Settings', icon: '⚙️' },
];

export function ClientLayout() {
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const { displayName, email, roles, logout } = useAuth();

  const initials = displayName
    ? displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : email?.slice(0, 2).toUpperCase() ?? '??';

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    if (profileOpen) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [profileOpen]);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <aside className="hidden md:flex w-64 flex-col bg-white border-r border-sand-200">
        <div className="px-4 pt-4 pb-3 border-b border-sand-200">
          <Link to="/" aria-label="Conscierra home" className="block">
            <img
              src="/brand/conscierra-logo-fern.png"
              alt="Conscierra"
              className="h-8 w-auto"
              width={183}
              height={59}
            />
          </Link>
          <p className="text-[10px] uppercase tracking-[0.2em] text-sage-500 mt-2">Client Portal</p>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-1">
          {navItems.map(item => (
            <NavLink key={item.path} to={item.path}
              className={({ isActive }) => `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium ${isActive ? 'bg-sand-100 text-fern-600' : 'text-gray-700 hover:bg-sand-50 hover:text-fern-600'}`}>
              <span>{item.icon}</span><span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="h-16 flex items-center justify-between border-b border-sand-200 bg-white px-4">
          <Link to="/" aria-label="Conscierra home" className="md:hidden">
            <img
              src="/brand/conscierra-logo-fern.png"
              alt="Conscierra"
              className="h-7 w-auto"
              width={183}
              height={59}
            />
          </Link>
          <div className="flex-1" />
          <div className="relative" ref={profileRef}>
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-2 rounded-md p-1 hover:bg-sand-50"
              aria-label="User menu"
            >
              <div className="w-8 h-8 rounded-full bg-fern-600 flex items-center justify-center text-white text-xs font-medium">
                {initials}
              </div>
              <span className="hidden lg:block text-sm font-medium text-gray-800">{displayName}</span>
            </button>

            {profileOpen && (
              <div className="absolute right-0 mt-2 w-64 rounded-lg border border-sand-200 bg-white shadow-lg z-50">
                <div className="px-4 py-3 border-b border-sand-200">
                  <p className="text-sm font-medium text-gray-900">{displayName}</p>
                  <p className="text-xs text-sage-500 truncate">{email}</p>
                  {roles.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {roles.map(r => (
                        <span key={r} className="inline-flex items-center rounded bg-sand-100 px-1.5 py-0.5 text-[10px] font-medium text-fern-600">{r}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="py-1">
                  <button
                    onClick={() => { setProfileOpen(false); logout(); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-6"><div className="mx-auto max-w-5xl"><Outlet /></div></main>
      </div>
    </div>
  );
}
